import React from "react";
import { graphql } from "gatsby";
import PostTitle from "../components/post-title";
import NavHeader from "../components/nav-header";
import PinCoverSet from "../components/pin-cover-set";
import BannerImage from "../components/banner-image";
import StandardPage from "../components/standard-page";
import StandardSection from "../components/standard-section";
import TagButtonStrip from "../components/tag-button-strip";
import RelatedPostCards from "../components/related-post-cards";
import NeighborPostLinks from "../components/neighbor-post-links";
import Seo from "../components/seo";
import SectionBreak from "../components/section-break";
import EmailRegister from "../components/email-register";
import { Link } from "gatsby";
import ContentRender from "../components/content-render";
import AdTinybanner from "../components/ad-tinybanner";
import TableOfContents from "../components/table-of-contents";

function renderTableOfContentsNode(toc) {
  return (
    <ul>
      {toc.items?.map((item) => (
        <li key={item.url}>
          <p>
            <a href={item.url}>{item.title}</a>
          </p>
          {renderTableOfContentsNode(item)}
        </li>
      ))}
    </ul>
  );
}

export default function Template({
  data, // this prop will be injected by the GraphQL query below.
  pageContext,
}) {
  const { mdx } = data;
  const { frontmatter, fields, body, excerpt, tableOfContents } = mdx;

  return (
    <StandardPage
      header={
        <NavHeader>
          <BannerImage childImageSharp={frontmatter.banner?.childImageSharp} />
        </NavHeader>
      }
    >
      <StandardSection className="space-y-8">
        <PostTitle
          title={frontmatter.title}
          subtitle={new Date(fields.date).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
          category={
            frontmatter.category ||
            frontmatter.categories?.split(" ").slice(-1)[0] ||
            "misc"
          }
        />

        {frontmatter.affiliate && (
          <figcaption>
            This post contains affiliate links, see our{" "}
            <Link to="/tos#affiliate-disclaimer">disclaimer</Link> for more
            information.
          </figcaption>
        )}

        {frontmatter.toc && (
          <TableOfContents tableOfContents={tableOfContents} />
        )}

        {frontmatter.ads && <AdTinybanner />}

        <ContentRender className="space-y-8" tableOfContents={tableOfContents}>
          {body}
        </ContentRender>
      </StandardSection>
      <StandardSection>
        {frontmatter.pins && (
          <PinCoverSet
            covers={frontmatter.pins}
            path={fields.slug}
            description={
              frontmatter.pinDescription || frontmatter.description || excerpt
            }
          />
        )}
        {(frontmatter.categories || frontmatter.tags) && (
          <TagButtonStrip
            categories={frontmatter.categories
              .split(" ")
              .concat(frontmatter.tags || [])}
            title={frontmatter.title}
            cover={frontmatter?.pins ? frontmatter?.pins[0] : undefined}
            description={excerpt}
            slug={fields.slug}
          />
        )}
        <div className="border-secondary border-b w-full h-1 -mt-6" />

        <NeighborPostLinks
          next={pageContext.next}
          previous={pageContext.previous}
        />
      </StandardSection>

      <SectionBreak>
        <StandardSection title="Let's stay in touch!" className="space-y-8">
          <p className="text-2xl text-center">
            Join the Lost with Lydia email list to get monthly travel guides and
            tips!
          </p>

          <EmailRegister />
        </StandardSection>
      </SectionBreak>

      <StandardSection>
        {frontmatter.related && (
          <RelatedPostCards posts={frontmatter.related} className="py-8" />
        )}
      </StandardSection>

      <Seo
        title={frontmatter.title}
        description={frontmatter.description || excerpt}
        cover={frontmatter.banner}
        slug={fields.slug}
      />
    </StandardPage>
  );
}

export const pageQuery = graphql`
  query ($id: String!) {
    mdx(id: { eq: $id }) {
      body
      tableOfContents(maxDepth: 3)
      frontmatter {
        date(formatString: "MMMM DD, YYYY")
        title
        toc
        categories
        tags
        affiliate
        ads
        pinDescription
        description
        banner {
          publicURL
          childImageSharp {
            fluid(quality: 90, maxWidth: 1920) {
              ...GatsbyImageSharpFluid
            }
          }
        }
        pins {
          id
          publicURL
          childImageSharp {
            gatsbyImageData(width: 1000, height: 1500)
          }
        }
        related {
          frontmatter {
            title
            banner {
              childImageSharp {
                gatsbyImageData(width: 400)
              }
            }
          }
          fields {
            slug
          }
          id
        }
      }
      fields {
        slug
        date
      }
      id
      excerpt
    }
  }
`;
